import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  FiEye,
  FiRefreshCw,
  FiSearch,
  FiX,
  FiFileText,
  FiCheckCircle,
  FiCircle,
  FiLock,
  FiArrowLeft,
  FiPlus,
  FiEdit,
  FiArchive,
  FiRotateCcw,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import TmisLayout from "../components/TmisLayout";
import "../App.css";

const API_BASE = process.env.REACT_APP_API || "http://localhost:3023/api";

const API_ENDPOINTS = {
  getTemplates: `${API_BASE}/getTemplates`,
  getArchivedTemplates: `${API_BASE}/getArchivedTemplates`,
  getTemplateDetails: (templateId) =>
    `${API_BASE}/getTemplateDetails/${templateId}`,
  deleteTemplate: (templateId) => `${API_BASE}/deleteTemplate/${templateId}`,
  unarchiveTemplate: (templateId) =>
    `${API_BASE}/unarchiveTemplate/${templateId}`,
};

const getAuthToken = () =>
  localStorage.getItem("authToken") || sessionStorage.getItem("authToken");

const getAxiosConfig = () => {
  const token = getAuthToken();
  if (!token) return {};
  return { headers: { Authorization: `Bearer ${token}` } };
};

const getLoginUser = () => {
  try {
    const storedUser =
      localStorage.getItem("user") || sessionStorage.getItem("user");
    if (!storedUser) return null;
    return JSON.parse(storedUser);
  } catch {
    return null;
  }
};

const getTypeLabel = (type = "") => {
  const value = String(type).toLowerCase();
  if (value === "radio") return "Radio Buttons";
  if (value === "multiple" || value === "multiple_choice")
    return "Multiple Choice";
  if (value === "short_answer") return "Short Answer";
  if (value === "long_answer") return "Long Answer";
  return type || "-";
};

const formatDate = (dateValue) => {
  if (!dateValue) return "-";

  try {
    const date = new Date(dateValue);

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    const hour = String(date.getHours()).padStart(2, "0");
    const minute = String(date.getMinutes()).padStart(2, "0");

    return `${day}-${month}-${year} ${hour}:${minute}`;
  } catch {
    return "-";
  }
};

const UnauthorizedPage = () => {
  const navigate = useNavigate();

  return (
    <TmisLayout showSidebar={false}>
      <main className="view-template-unauthorized-main">
        <div className="view-template-unauthorized-card">
          <div className="view-template-lock-animation">
            <div className="view-template-lock-circle">
              <FiLock />
            </div>
            <span className="view-template-pulse-ring"></span>
            <span className="view-template-pulse-ring second"></span>
          </div>

          <h1>Access Denied</h1>
          <p>You are not authorized to view assessment templates.</p>

          <button
            type="button"
            className="view-template-back-btn"
            onClick={() => navigate("/dashboard")}
          >
            <FiArrowLeft />
            Back to Dashboard
          </button>
        </div>
      </main>
    </TmisLayout>
  );
};

const ViewPerformanceTemplate = () => {
  const loginUser = useMemo(() => getLoginUser(), []);
  const role = String(loginUser?.role || "").toLowerCase();

  if (role === "trainer") return <UnauthorizedPage />;

  return <ViewPerformanceTemplateContent />;
};

const ViewPerformanceTemplateContent = () => {
  const navigate = useNavigate();
  const loginUser = useMemo(() => getLoginUser(), []);

  const [searchText, setSearchText] = useState("");
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [elements, setElements] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [message, setMessage] = useState("");
  const [showElementModal, setShowElementModal] = useState(false);
  const [showArchive, setShowArchive] = useState(false);

  const loadTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    setMessage("");

    try {
      const endpoint = showArchive
        ? API_ENDPOINTS.getArchivedTemplates
        : API_ENDPOINTS.getTemplates;

      const response = await axios.get(endpoint, getAxiosConfig());
      const rows = response.data?.data || response.data?.templates || [];

      setTemplates(Array.isArray(rows) ? rows : []);
    } catch (error) {
      setMessage(
        error.response?.data?.message || "Failed to load assessment templates."
      );
    } finally {
      setLoadingTemplates(false);
    }
  }, [showArchive]);

  const loadTemplateDetails = useCallback(async (templateId) => {
    if (!templateId) return;

    setLoadingDetails(true);
    setMessage("");

    try {
      const response = await axios.get(
        API_ENDPOINTS.getTemplateDetails(templateId),
        getAxiosConfig()
      );

      setSelectedTemplate(response.data?.template || null);
      setElements(
        Array.isArray(response.data?.questions) ? response.data.questions : []
      );
    } catch (error) {
      setSelectedTemplate(null);
      setElements([]);
      setMessage(
        error.response?.data?.message || "Failed to load template details."
      );
    } finally {
      setLoadingDetails(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const filteredTemplates = useMemo(() => {
    const keyword = searchText.trim().toLowerCase();
    if (!keyword) return templates;

    return templates.filter(
      (template) =>
        String(template.title || "").toLowerCase().includes(keyword) ||
        String(template.description || "").toLowerCase().includes(keyword)
    );
  }, [searchText, templates]);

  const handleCreateTemplate = () => {
    navigate("/performanceTemplate");
  };

  const handleViewElement = async (template) => {
    await loadTemplateDetails(template.template_id || template.id);
    setShowElementModal(true);
  };

  const handlePreviewForm = (template) => {
    navigate("/previewAssessment", {
      state: {
        template_id: template.template_id || template.id,
      },
    });
  };

  const handleEditTemplate = (template) => {
    navigate("/performanceTemplate", {
      state: {
        mode: "edit",
        template_id: template.template_id || template.id,
      },
    });
  };

  const handleArchiveTemplate = async (template) => {
    const templateId = template.template_id || template.id;

    if (
      !window.confirm(
        `Are you sure you want to archive "${
          template.title || "this template"
        }"?`
      )
    ) {
      return;
    }

    try {
      await axios.post(
        API_ENDPOINTS.deleteTemplate(templateId),
        {
          created_by: loginUser?.id,
        },
        getAxiosConfig()
      );

      setMessage("Template archived successfully.");
      await loadTemplates();
    } catch (error) {
      setMessage(error.response?.data?.message || "Failed to archive template.");
    }
  };

  const handleUnarchiveTemplate = async (template) => {
    const templateId = template.template_id || template.id;

    if (
      !window.confirm(
        `Are you sure you want to unarchive "${
          template.title || "this template"
        }"?`
      )
    ) {
      return;
    }

    try {
      await axios.post(
        API_ENDPOINTS.unarchiveTemplate(templateId),
        {
          created_by: loginUser?.id,
        },
        getAxiosConfig()
      );

      setMessage("Template unarchived successfully.");
      await loadTemplates();
    } catch (error) {
      setMessage(
        error.response?.data?.message || "Failed to unarchive template."
      );
    }
  };

  const toggleArchiveView = () => {
    setTemplates([]);
    setSearchText("");
    setMessage("");
    setShowArchive((prev) => !prev);
  };

  return (
    <TmisLayout
      showSidebar={false}
      searchText={searchText}
      onSearchChange={setSearchText}
      searchPlaceholder="Search template title or description..."
    >
      <main className="view-template-main">
        <section className="view-template-card view-template-hero">
          <div>
            <h1>
              {showArchive
                ? "Archived Assessment Templates"
                : "View Assessment Template"}
            </h1>
            <p>
              {showArchive
                ? "View archived templates and restore them when needed."
                : "View all assessment templates, elements, preview form, edit, or archive templates."}
            </p>
          </div>

          <div className="view-template-hero-actions">
            {!showArchive && (
              <button
                type="button"
                className="view-template-create-btn"
                onClick={handleCreateTemplate}
              >
                <FiPlus />
                Create Template
              </button>
            )}

            <button
              type="button"
              className="view-template-action-btn preview"
              onClick={toggleArchiveView}
            >
              {showArchive ? <FiArrowLeft /> : <FiArchive />}
              {showArchive ? "View Active" : "View Archive"}
            </button>

            <button
              type="button"
              className="view-template-refresh-btn"
              onClick={loadTemplates}
              disabled={loadingTemplates || loadingDetails}
            >
              <FiRefreshCw />
              {loadingTemplates || loadingDetails ? "Loading..." : "Refresh"}
            </button>
          </div>
        </section>

        {message && <div className="view-template-message">{message}</div>}

        <section className="view-template-card view-template-filter-card">
          <label className="view-template-field">
            <span>Search Template</span>
            <div className="view-template-search-box">
              <FiSearch />
              <input
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search template title or description"
              />
            </div>
          </label>
        </section>

        <section className="view-template-card view-template-table-card">
          {loadingTemplates ? (
            <div className="view-template-loading">Loading templates...</div>
          ) : filteredTemplates.length === 0 ? (
            <div className="view-template-empty-card small">
              <FiFileText />
              <h2>
                {showArchive
                  ? "No archived templates found"
                  : "No assessment templates found"}
              </h2>
              <p>
                {showArchive
                  ? "There are no archived templates yet."
                  : "Please create a new assessment template first."}
              </p>
            </div>
          ) : (
            <div className="view-template-table-wrapper">
              <table className="view-template-table">
                <thead>
                  <tr>
                    <th style={{ width: "70px" }}>No.</th>
                    <th style={{ width: "210px" }}>Template Title</th>
                    <th style={{ width: "270px" }}>Description</th>
                    <th style={{ width: "190px" }}>Created Date</th>
                    <th style={{ width: "420px" }}>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredTemplates.map((template, index) => (
                    <tr key={template.template_id || template.id}>
                      <td>{index + 1}</td>

                      <td>
                        <strong>{template.title || "Untitled Template"}</strong>
                      </td>

                      <td>{template.description || "-"}</td>

                      <td>{formatDate(template.created_at)}</td>

                      <td>
                        <div className="view-template-action-group">
                          <button
                            type="button"
                            className="view-template-action-btn view"
                            onClick={() => handleViewElement(template)}
                          >
                            <FiFileText />
                            View Element
                          </button>

                          {!showArchive && (
                            <>
                              <button
                                type="button"
                                className="view-template-action-btn preview"
                                onClick={() => handlePreviewForm(template)}
                              >
                                <FiEye />
                                Preview Form
                              </button>

                              <button
                                type="button"
                                className="view-template-action-btn edit"
                                onClick={() => handleEditTemplate(template)}
                              >
                                <FiEdit />
                                Edit
                              </button>

                              <button
                                type="button"
                                className="view-template-action-btn archive"
                                onClick={() => handleArchiveTemplate(template)}
                              >
                                <FiArchive />
                                Archive
                              </button>
                            </>
                          )}

                          {showArchive && (
                            <button
                              type="button"
                              className="view-template-action-btn edit"
                              onClick={() => handleUnarchiveTemplate(template)}
                            >
                              <FiRotateCcw />
                              Unarchive
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>

      {showElementModal && selectedTemplate && (
        <div className="view-template-modal-backdrop">
          <div
            className="view-template-modal large"
            role="dialog"
            aria-modal="true"
          >
            <div className="view-template-modal-header">
              <div>
                <h2>{selectedTemplate.title || "Untitled Template"}</h2>
                <p>{selectedTemplate.description || "No description added."}</p>
              </div>

              <button
                type="button"
                onClick={() => setShowElementModal(false)}
                aria-label="Close Element"
              >
                <FiX />
              </button>
            </div>

            {loadingDetails ? (
              <div className="view-template-loading">Loading elements...</div>
            ) : elements.length === 0 ? (
              <div className="view-template-empty-state">
                No elements found for this template.
              </div>
            ) : (
              <div className="view-template-question-list">
                {elements.map((item, index) => {
                  const answers = item.answers || [];
                  const type = String(item.type || "").toLowerCase();
                  const isChoice =
                    type === "radio" ||
                    type === "multiple" ||
                    type === "multiple_choice";

                  return (
                    <article
                      className="view-template-question-card"
                      key={item.element_id || index}
                    >
                      <div className="view-template-question-header">
                        <div>
                          <h3>
                            Element {index + 1}
                            {Number(item.is_required) === 1 && (
                              <span>Required</span>
                            )}
                          </h3>

                          <p>{item.element || "Untitled Element"}</p>
                        </div>

                        <strong>{getTypeLabel(item.type)}</strong>
                      </div>

                      {isChoice && (
                        <div className="view-template-answer-list">
                          {answers.length === 0 ? (
                            <div className="view-template-no-answer">
                              No answers added.
                            </div>
                          ) : (
                            answers.map((answer, answerIndex) => (
                              <div
                                className="view-template-answer-item"
                                key={answer.answer_id || answerIndex}
                              >
                                {type === "multiple" ||
                                type === "multiple_choice" ? (
                                  <FiCheckCircle />
                                ) : (
                                  <FiCircle />
                                )}
                                <span>{answer.answer}</span>
                              </div>
                            ))
                          )}
                        </div>
                      )}

                      {type === "short_answer" && (
                        <div className="view-template-answer-preview">
                          Short answer text field
                        </div>
                      )}

                      {type === "long_answer" && (
                        <div className="view-template-answer-preview long">
                          Long answer text area
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </TmisLayout>
  );
};

export default ViewPerformanceTemplate;